import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, CreditCard, CheckCircle, XCircle, ArrowLeft, ShoppingCart } from 'lucide-react';

const ProfilePage: React.FC = () => {
  const { user, userProfile, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <Link 
              to="/generator"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Generator</span>
            </Link>
            <button
              onClick={handleSignOut}
              className="text-red-600 hover:text-red-700 font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg">
          {/* Profile Header */}
          <div className="px-6 py-8 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="bg-primary-100 p-4 rounded-full">
                <User className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
                <p className="text-gray-600">Manage your account settings and view your usage</p>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-6 space-y-8">
            {/* Account Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email Address</p>
                    <p className="text-gray-900">{user?.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {user?.email_confirmed_at ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email Verification</p>
                    <p className={user?.email_confirmed_at ? 'text-green-600' : 'text-red-600'}>
                      {user?.email_confirmed_at ? 'Verified' : 'Not verified'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Credits Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Credits & Usage</h2>
              <div className="bg-gradient-to-r from-primary-50 to-indigo-50 rounded-lg p-6 border border-primary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-8 w-8 text-primary-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Available Credits</p>
                      <p className="text-3xl font-bold text-primary-600">
                        {userProfile?.credits ?? 0}
                      </p>
                    </div>
                  </div>
                  
                  {(userProfile?.credits ?? 0) <= 0 && (
                    <div className="text-center">
                      <p className="text-sm text-red-600 mb-2">You've used all your free credits!</p>
                      <Link
                        to="/buy-credits"
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span>Buy More Credits</span>
                      </Link>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-primary-200">
                  <p className="text-sm text-gray-600">
                    Each email generation uses 1 credit. You started with 5 free credits when you signed up.
                  </p>
                </div>
              </div>
            </div>

            {/* Account Actions */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/generator"
                  className="bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-lg font-medium text-center transition-colors"
                >
                  Generate Cold Emails
                </Link>
                <Link
                  to="/buy-credits"
                  className="bg-gray-100 hover:bg-gray-200 text-gray-900 p-4 rounded-lg font-medium text-center transition-colors"
                >
                  Buy More Credits
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;